import { useState, useCallback, useEffect, useRef } from 'react';
import { getServicesSearch } from 'services/services/serviceLists';
import { useDispatch } from 'react-redux';
import { showError } from '../../../../../redux/common/actions';
import { debounce } from 'helpers/utils';

export const useServices = (siteAppID, departmentFilter, timeframeFilter, updateFilter, filterValues, sortDirection = 'asc', sortField= 'scheduledDate') => {
  const dispatch = useDispatch();
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasInteracted, setHasInteracted] = useState(false);

  const lastDepartmentId   = useRef('');
  const lastTimeframe      = useRef({ fromDate: '', toDate: '' });
  const lastSortField      = useRef(sortField);
  const lastSortDirection  = useRef(sortDirection);

  const fetchServices = useCallback(async (query = '') => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      const params = {
        siteAppId:      siteAppID,
        search:         query,
        pageSize:       20,
        pageNumber:     1,
        statusType:     "C",
        sortField:      sortField,             
        sort:           sortDirection,            // 'asc' | 'desc'
        chips:          filterValues?.chips?.value || [],
      };

     // Add department filter if available
      const deptValue = departmentFilter?.value;
      if (Array.isArray(deptValue)) {
        const ids = deptValue
          .map(d => parseInt(d.id, 10))
          .filter(id => !isNaN(id) && id !== 0);
        if (ids.length) params.siteDepartmentIds = ids;
      } else if (deptValue?.id && deptValue.id !== '' && deptValue.id !== 'all') {
        const id = parseInt(deptValue.id, 10);
        if (!isNaN(id) && id !== 0) params.siteDepartmentIds = [id];
      }

     // Add timeframe filters if available
      if (timeframeFilter?.value) {
        if (timeframeFilter.value.fromDate) params.fromDate = timeframeFilter.value.fromDate;
        if (timeframeFilter.value.toDate)   params.toDate   = timeframeFilter.value.toDate;
      }

      const response = await getServicesSearch(params);

      if (response.status) {
        const results = Array.isArray(response.data) ? response.data : [];
        setServices(results);

        // clear invalid selection
        if (filterValues?.service?.value?.id) {
          if (!results.some(s => s.id === filterValues.service.value.id)) {
            updateFilter('service', {}, { serviceId: '' });
          }
        }
      } else {
        dispatch(showError(response.data?.message || 'Failed to load services'));
      }
    } catch (err) {
      console.error('Services fetch error:', err);
      dispatch(showError('Error loading services'));
    } finally {
      setIsLoading(false);
    }
  }, [
    siteAppID, departmentFilter, timeframeFilter, filterValues,
    sortField, sortDirection, updateFilter, dispatch, isLoading
  ]);

    // Debounced search function
  const debouncedSearch = useCallback(
    debounce((query) => {
      fetchServices(query);
    }, 300),
    [fetchServices]
  );

  // Handle search input change
  const handleSearchChange = useCallback((query) => {
    setSearchQuery(query);
    debouncedSearch(query);
  }, [debouncedSearch]);
  
  // Update data when department or timeframe filters change, but only if dropdown has been used
  useEffect(() => {
    if (!hasInteracted) return;

    // Check if department or timeframe actually changed
    // Handle both single and multiselect department formats
    const departmentValue = departmentFilter?.value;
    let currentDepartmentId;
    if (Array.isArray(departmentValue)) {
      // For multiselect, create a sorted string of IDs
      currentDepartmentId = departmentValue
        .map(dept => dept?.id || '')
        .filter(id => id !== '' && id !== 'all')
        .sort()
        .join(',');
    } else {
      // For single select
      currentDepartmentId = departmentValue?.id || '';
    }

    const currentFromDate = timeframeFilter?.value?.fromDate || '';
    const currentToDate = timeframeFilter?.value?.toDate || '';

    const departmentChanged = currentDepartmentId !== lastDepartmentId.current;
    const timeframeChanged =
      currentFromDate !== lastTimeframe.current.fromDate ||
      currentToDate !== lastTimeframe.current.toDate;
    const sortChanged = sortDirection !== lastSortDirection.current;

    // Only fetch if something actually changed
    if (departmentChanged || timeframeChanged || sortChanged) {
      // Update refs to current values
      lastDepartmentId.current = currentDepartmentId;
      lastTimeframe.current = {
        fromDate: currentFromDate,
        toDate: currentToDate
      };
      lastSortDirection.current = sortDirection;

      fetchServices(searchQuery);
    }
  }, [departmentFilter, timeframeFilter, hasInteracted, fetchServices, searchQuery, sortDirection]);


  // Only showing changed parts below...

  useEffect(() => {
    if (!hasInteracted) return;

    const deptValue = departmentFilter?.value;
    let currDept = '';
    if (Array.isArray(deptValue)) {
      currDept = deptValue
        .map(d => d?.id || '')
        .filter(id => id && id !== 'all')
        .sort()
        .join(',');
    } else {
      currDept = deptValue?.id || '';
    }

    const from = timeframeFilter?.value?.fromDate || '';
    const to   = timeframeFilter?.value?.toDate   || '';

    const changed =
      currDept           !== lastDepartmentId.current ||
      from               !== lastTimeframe.current.fromDate ||
      to                 !== lastTimeframe.current.toDate ||
      sortField          !== lastSortField.current ||
      sortDirection      !== lastSortDirection.current;

    if (changed) {
      lastDepartmentId.current   = currDept;
      lastTimeframe.current      = { fromDate: from, toDate: to };
      lastSortField.current      = sortField;
      lastSortDirection.current  = sortDirection;

      fetchServices(searchQuery);
    }
  }, [
    departmentFilter, timeframeFilter, sortField, sortDirection,
    hasInteracted, fetchServices, searchQuery
  ]);

  const handleDropdownClick = useCallback(() => {
    setHasInteracted(true);

    lastSortField.current     = sortField;
    lastSortDirection.current = sortDirection;

    return fetchServices(searchQuery);
  }, [departmentFilter, timeframeFilter, sortField, sortDirection, fetchServices, searchQuery]);

  return {
    services,
    isLoading,
    searchQuery,
    handleSearchChange,
    handleDropdownClick,
  };
};